import { Dayjs } from "dayjs";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { TextAlign } from "../../../enums";
import { KeyName } from "../../../keyName";
import { IInputOnChangeEvent, IInputProps } from "../input";
import { StyledNumericInput } from "./DayInput.styles";

export interface IProps extends Omit<IInputProps, "value" | "onChange"> {
    date: Dayjs;
    value: number;
    onChange: (val: number, date: Dayjs) => void;
    onDaySelect: (date: Dayjs) => void;
    isSelected: boolean;
    hasError: boolean;
}

const ReadOnlyWrapper = styled.div`
    cursor: pointer;
`;

const DayInput: React.FC<IProps> = ({
                                        date,
                                        value,
                                        onChange,
                                        onDaySelect,
                                        isSelected,
                                        hasError,
                                        isReadOnly
                                    }) => {
    const { t } = useTranslation("Components");
    const handleChange = useCallback((args: IInputOnChangeEvent<number>) => {
        onChange(args.value, date);
    }, [onChange, date]);

    const handleSelect = useCallback(() => {
        onDaySelect(date);
    }, [onDaySelect, date]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === KeyName.Enter) {
            onDaySelect(date);
        }
    }, [onDaySelect, date]);

    const input = <StyledNumericInput
        $isSelected={isSelected}
        $hasError={hasError}
        onClick={!isReadOnly ? handleSelect : undefined}
        onKeyPress={!isReadOnly ? handleKeyPress : undefined}
        placeholder={t("Components:Calendar.HourPlaceholder")}
        value={value}
        onChange={handleChange}
        textAlign={TextAlign.Right}
        isReadOnly={isReadOnly}/>;

    if (isReadOnly) {
        return <ReadOnlyWrapper onClick={handleSelect} onKeyPress={handleKeyPress}>{input}</ReadOnlyWrapper>;
    }

    return <>{input}</>;
};

export default DayInput;